import { getConfig } from "@src/config";
import { GQLFinanceType } from "@src/graphql.apollo.generated";
import * as Cancel from "@src/shared/assets/images/cancel.inline.svg";
import Button, { ButtonFontWeight, ButtonSize, ButtonVariant, ButtonWidth } from "@src/shared/button/Button";
import { getDefaultFilter } from "@src/shared/context/searchfilter/IFilter";
import {
    firstRegistrationYearOptions,
    getKmPerYearOptions,
    kmFilterOptions,
    useFinanceRuntimeOptions,
    useFinanceTypeOptions,
    usePsFilterOptions,
} from "@src/shared/context/searchfilter/Options";
import useSearchFilter from "@src/shared/context/searchfilter/useSearchFilter";
import useSearchResult from "@src/shared/context/searchresult/useSearchResult";
import LoadingSpinner, { LoadingSpinnerVariant } from "@src/shared/loadingSpinner/LoadingSpinner";
import FormattedCurrency from "@src/shared/utils/FormattedCurrency";
import { getCurrencySignFromDomain } from "@src/skeleton/intlProvider/CustomIntlProvider";
import { useScrollRestoration } from "gatsby";
import * as React from "react";
import { FormattedMessage, FormattedNumber, useIntl } from "react-intl";

import { ReactSelectOptions } from "../../../types";
import ColorFilter from "./filter/colorfilter/ColorFilter";
import InputFilter from "./filter/inputfilter/InputFilter";
import MultipleChoiceFilter from "./filter/multiplechoicefilter/MultipleChoiceFilter";
import RadioButtonFilter from "./filter/radiobuttonfilter/RadioButtonFilter";
import SingleChoiceFilter from "./filter/singlechoicefilter/SingleChoiceFilter";
import SliderFilter from "./filter/sliderfilter/SliderFilter";
import { sliderMinMaxValues } from "./filter/sliderfilter/sliderMinMaxValues";
import * as sc from "./SearchFilter.sc";
import { convertToSelectOptions } from "./SearchFilterHelper";
import SearchSort from "./SearchSort";
import CaptionContainer from "./withcaption/CaptionContainer";

export interface IFormValues {
    brand: ReactSelectOptions<string> | null;
    modelGroup: ReactSelectOptions<string> | null;
    fuel: ReactSelectOptions<string>[] | null;
    province: ReactSelectOptions<string>[] | null;
}

const SearchFilter: React.FunctionComponent = () => {
    const filter = useSearchFilter();
    const searchResult = useSearchResult();
    const intl = useIntl();
    const financeTypeOptions = useFinanceTypeOptions();
    const financeRuntimeOptions = useFinanceRuntimeOptions(getConfig("scope").domain);
    const filterOptionsMinPs = usePsFilterOptions(filter.filter.minPs);
    const filterOptionsMaxPs = usePsFilterOptions(undefined, filter.filter.maxPs);

    const filterScrollRestoration = useScrollRestoration("search-filter");

    const handleAdjustFilterClick = () => filter.setMobileFilterOpen(true);

    const handleShowResultsClick = () => {
        filter.setMobileFilterOpen(false);
        window.scrollTo({ top: 240, left: 0, behavior: "smooth" });
    };

    const handleFilterCancel = () => filter.setMobileFilterOpen(false);

    const carTypes = [
        {
            value: "usedCar",
            label: intl.formatMessage({ id: "leasme.filter.carType.usedCar", defaultMessage: "Gebrauchtwagen" }),
        },
        {
            value: "newCar",
            label: intl.formatMessage({ id: "leasme.filter.carType.newCar", defaultMessage: "Neuwagen" }),
        },
    ];

    const handleFilterReset = () => {
        filter.resetFilter?.();
        filter.setMobileFilterOpen(false);
    };

    const sliderValues = sliderMinMaxValues(getConfig("scope").domain);

    return (
        <sc.Root>
            <sc.SearchHeadline>
                <FormattedMessage id="leaseme.searchcriteria" defaultMessage="Suchkriterien" />
            </sc.SearchHeadline>
            <sc.MobileHeader>
                <SliderFilter
                    value={filter.filter.maximumRate}
                    minValue={sliderValues.min}
                    maxValue={sliderValues.max}
                    onChange={(value) => {
                        filter.setFilter?.("maximumRate", value);
                    }}
                    currencySign={getCurrencySignFromDomain(getConfig("scope").domain)}
                />
                <sc.Stats>
                    <sc.Stat onClick={handleAdjustFilterClick}>
                        <sc.StatHeadline>
                            <FormattedMessage
                                id="leaseme.runtime.value"
                                defaultMessage="{financeRuntime} Monate"
                                values={{
                                    financeRuntime: intl.formatNumber(filter.filter.financeRuntime),
                                }}
                            />
                        </sc.StatHeadline>
                        <sc.StatDescription>
                            <FormattedMessage id="leaseme.runtime.label" defaultMessage="Laufzeit" />
                        </sc.StatDescription>
                    </sc.Stat>
                    {getConfig("scope").domain !== "car4me.si" && (
                        <>
                            {filter.filter.financeType === GQLFinanceType.leasing ? (
                                <sc.Stat onClick={handleAdjustFilterClick}>
                                    <sc.StatHeadline>
                                        <FormattedNumber value={filter.filter.kmPerYear} /> <FormattedMessage id="leaseme.km" defaultMessage="km" />
                                    </sc.StatHeadline>
                                    <sc.StatDescription>
                                        <FormattedMessage id="leaseme.perYear" defaultMessage="pro Jahr" />
                                    </sc.StatDescription>
                                </sc.Stat>
                            ) : (
                                <sc.Stat onClick={handleAdjustFilterClick}>
                                    <sc.StatHeadline>{filter.filter.remainingCredit}%</sc.StatHeadline>
                                    <sc.StatDescription>
                                        <FormattedMessage id="leaseme.remainingCredit.label" defaultMessage="Restkredit" />
                                    </sc.StatDescription>
                                </sc.Stat>
                            )}
                        </>
                    )}
                    <sc.Stat onClick={handleAdjustFilterClick}>
                        <sc.StatHeadline>
                            <FormattedCurrency value={filter.filter.downPayment} />
                        </sc.StatHeadline>
                        <sc.StatDescription>
                            <FormattedMessage id="leaseme.downPayment.label" defaultMessage="Eigenleistung" />
                        </sc.StatDescription>
                    </sc.Stat>
                </sc.Stats>
            </sc.MobileHeader>
            <sc.Filter
                open={filter.mobileFilterOpen}
                ref={filterScrollRestoration.ref as React.MutableRefObject<HTMLDivElement | null>}
                onScroll={filterScrollRestoration.onScroll}
            >
                <sc.FilterActions>
                    <Button size={ButtonSize.Small} onClick={handleFilterCancel} leftContainer={<Cancel />} fontWeight={ButtonFontWeight.Bold}>
                        <FormattedMessage id="leaseme.filter.cancel" defaultMessage="Vorgang Abbrechen" />
                    </Button>
                </sc.FilterActions>
                <sc.MobileSearchHeadline>
                    <FormattedMessage id="leaseme.searchcriteria" defaultMessage="Suchkriterien" />
                </sc.MobileSearchHeadline>
                <sc.FilterGroup>
                    <sc.SliderFilterContainer>
                        <SliderFilter
                            value={filter.filter.maximumRate}
                            minValue={sliderValues.min}
                            maxValue={sliderValues.max}
                            onChange={(value) => {
                                filter.setFilter?.("maximumRate", value);
                            }}
                            currencySign={getCurrencySignFromDomain(getConfig("scope").domain)}
                        />
                    </sc.SliderFilterContainer>

                    {getConfig("isCarhubActive") && getConfig("scope").domain === "car4me.porschebank.at" && (
                        <CaptionContainer>
                            <RadioButtonFilter
                                value={filter.filter.carType}
                                options={carTypes}
                                onChange={(value) => {
                                    filter.setFilter?.("carType", value);
                                }}
                            />
                        </CaptionContainer>
                    )}

                    {financeTypeOptions.length > 1 && (
                        <CaptionContainer
                            caption={intl.formatMessage({
                                id: "leaseme.financing",
                                defaultMessage: "Finanzierung",
                            })}
                        >
                            <SingleChoiceFilter<GQLFinanceType>
                                value={filter.filter.financeType}
                                options={financeTypeOptions}
                                onChange={(value) => {
                                    if (filter.setFilter) {
                                        filter.setFilter("financeType", value);
                                    }
                                }}
                            />
                        </CaptionContainer>
                    )}

                    <CaptionContainer
                        caption={intl.formatMessage({
                            id: "leaseme.runtime.label",
                            defaultMessage: "Laufzeit",
                        })}
                    >
                        <SingleChoiceFilter<number>
                            value={filter.filter.financeRuntime}
                            options={financeRuntimeOptions}
                            onChange={(value) => {
                                if (filter.setFilter) {
                                    filter.setFilter("financeRuntime", value);
                                }
                            }}
                        />
                    </CaptionContainer>
                    {getConfig("scope").domain !== "car4me.si" && filter.filter.financeType === GQLFinanceType.leasing && (
                        <CaptionContainer
                            caption={intl.formatMessage({
                                id: "leaseme.filter.kmPerYear",
                                defaultMessage: "Kilometer pro Jahr",
                            })}
                        >
                            <SingleChoiceFilter<number>
                                value={filter.filter.kmPerYear}
                                options={getKmPerYearOptions(getConfig("scope").domain)}
                                onChange={(value) => {
                                    if (filter.setFilter) {
                                        filter.setFilter("kmPerYear", value);
                                    }
                                }}
                            />
                        </CaptionContainer>
                    )}
                    <CaptionContainer
                        caption={intl.formatMessage({
                            id: "leaseme.downPayment.label",
                            defaultMessage: "Eigenleistung",
                        })}
                    >
                        <InputFilter
                            value={filter.filter.downPayment.toString()}
                            onChange={(value) => {
                                if (filter.setFilter && value) {
                                    filter.setFilter("downPayment", parseInt(value));
                                }
                            }}
                            startAdornment={
                                getConfig("scope").domain !== "car4me.bg" && getConfig("scope").domain !== "car4me.porschefinance.hu"
                                    ? getCurrencySignFromDomain(getConfig("scope").domain)
                                    : undefined
                            }
                            endAdornment={
                                getConfig("scope").domain !== "car4me.bg" && getConfig("scope").domain === "car4me.porschefinance.hu"
                                    ? getCurrencySignFromDomain(getConfig("scope").domain)
                                    : undefined
                            }
                            maxValue={9999999999}
                            minValue={0}
                            defaultValue={getDefaultFilter(getConfig("scope").domain).downPayment}
                            startAdornmentPadding={10}
                            endAdornmentPadding={10}
                            isCurrency
                            inputMode="numeric"
                        />
                        {getConfig("scope").domain !== "car4me.si" &&
                            searchResult.facets?.downPayment?.min &&
                            searchResult.facets.downPayment.min > filter.filter.downPayment &&
                            !searchResult.loading && (
                                <sc.FilterMessage>
                                    <FormattedMessage
                                        id="leaseme.downPayment.messageTooLow"
                                        defaultMessage="Keine Ergebnisse für die gesetzten Parameter. Bitte gib eine Eigenleistung von mindestens {minValue} ein."
                                        values={{
                                            minValue: <FormattedCurrency value={searchResult.facets.downPayment.min} />,
                                        }}
                                    />
                                </sc.FilterMessage>
                            )}
                        {searchResult.facets?.downPayment?.max &&
                            searchResult.facets.downPayment.max < filter.filter.downPayment &&
                            !searchResult.loading && (
                                <sc.FilterMessage>
                                    <FormattedMessage
                                        id="leaseme.downPayment.messageTooHigh"
                                        defaultMessage="Keine Ergebnisse für die gesetzten Parameter. Bitte gib eine Eigenleistung von maximal {maxValue} ein."
                                        values={{
                                            maxValue: <FormattedCurrency value={searchResult.facets.downPayment.max} />,
                                        }}
                                    />
                                </sc.FilterMessage>
                            )}
                    </CaptionContainer>
                </sc.FilterGroup>
                <sc.Headline>
                    <FormattedMessage id="leaseme.filter.offers" defaultMessage="Angebote filtern" />
                </sc.Headline>
                <sc.FilterGroup>
                    <CaptionContainer
                        caption={intl.formatMessage({
                            id: "leaseme.brand",
                            defaultMessage: "Marke",
                        })}
                    >
                        <MultipleChoiceFilter<string>
                            values={filter.filter.brands}
                            options={convertToSelectOptions(searchResult.facets?.brands)}
                            filterKey={"brands"}
                            onChange={(value) => {
                                if (filter.setFilter) {
                                    filter.setFilter("brands", value);
                                }
                            }}
                            placeholder={intl.formatMessage({
                                id: "leaseme.filter.allBrands",
                                defaultMessage: "Alle Marken",
                            })}
                            isSearchable={true}
                            isClearable={true}
                            selectAll
                        />
                    </CaptionContainer>

                    <CaptionContainer
                        caption={intl.formatMessage({
                            id: "leaseme.models.plural",
                            defaultMessage: "Modelle",
                        })}
                    >
                        <MultipleChoiceFilter<string>
                            values={filter.filter.modelGroups}
                            options={convertToSelectOptions(searchResult.facets?.modelGroups)}
                            onChange={(value) => {
                                if (filter.setFilter) {
                                    filter.setFilter("modelGroups", value);
                                }
                            }}
                            placeholder={intl.formatMessage({
                                id: "leaseme.filter.allModels",
                                defaultMessage: "Alle Modelle",
                            })}
                            isSearchable={true}
                            isClearable={true}
                            isDisabled={!filter.filter.brands}
                            selectAll
                        />
                    </CaptionContainer>
                    <CaptionContainer
                        caption={intl.formatMessage({
                            id: "leaseme.color",
                            defaultMessage: "Farbe",
                        })}
                        isVisible={
                            !searchResult.loading &&
                            searchResult.facets?.colors != null &&
                            searchResult.facets.colors.length > 0 &&
                            convertToSelectOptions(searchResult.facets?.colors).length > 0
                        }
                    >
                        <ColorFilter
                            selectedColors={filter.filter.colors}
                            options={convertToSelectOptions(searchResult.facets?.colors)}
                            onChange={(values) => {
                                if (filter.setFilter) {
                                    filter.setFilter("colors", values);
                                }
                            }}
                        />
                    </CaptionContainer>

                    <CaptionContainer
                        caption={intl.formatMessage({
                            id: "leaseme.mileage",
                            defaultMessage: "Kilometerstand",
                        })}
                    >
                        <sc.Row>
                            <sc.Col>
                                <SingleChoiceFilter<number>
                                    value={filter.filter.minKm}
                                    options={kmFilterOptions(getConfig("scope").domain, filter.filter.minKm)}
                                    onChange={(value) => {
                                        if (filter.setFilter) {
                                            filter.setFilter("minKm", value);
                                        }
                                    }}
                                    placeholder={intl.formatMessage({
                                        id: "leaseme.from",
                                        defaultMessage: "von",
                                    })}
                                    isClearable={true}
                                    isSearchable={true}
                                />
                            </sc.Col>
                            <sc.Col>
                                <SingleChoiceFilter<number>
                                    value={filter.filter.maxKm}
                                    options={kmFilterOptions(getConfig("scope").domain, filter.filter.minKm)}
                                    onChange={(value) => {
                                        if (filter.setFilter) {
                                            filter.setFilter("maxKm", value);
                                        }
                                    }}
                                    placeholder={intl.formatMessage({
                                        id: "leaseme.to",
                                        defaultMessage: "bis",
                                    })}
                                    isClearable={true}
                                    isSearchable={true}
                                />
                            </sc.Col>
                        </sc.Row>
                    </CaptionContainer>
                    <CaptionContainer
                        caption={intl.formatMessage({
                            id: "leaseme.firstRegistration",
                            defaultMessage: "Erstzulassung",
                        })}
                    >
                        <sc.Row>
                            <sc.Col>
                                <SingleChoiceFilter<number>
                                    value={filter.filter.minFirstRegistration}
                                    options={firstRegistrationYearOptions(undefined, filter.filter.maxFirstRegistration)}
                                    onChange={(value) => {
                                        if (filter.setFilter) {
                                            filter.setFilter("minFirstRegistration", value);
                                        }
                                    }}
                                    placeholder={intl.formatMessage({
                                        id: "leaseme.from",
                                        defaultMessage: "von",
                                    })}
                                    isClearable={true}
                                    isSearchable={true}
                                />
                            </sc.Col>
                            <sc.Col>
                                <SingleChoiceFilter<number>
                                    value={filter.filter.maxFirstRegistration}
                                    options={firstRegistrationYearOptions(filter.filter.minFirstRegistration)}
                                    onChange={(value) => {
                                        if (filter.setFilter) {
                                            filter.setFilter("maxFirstRegistration", value);
                                        }
                                    }}
                                    placeholder={intl.formatMessage({
                                        id: "leaseme.to",
                                        defaultMessage: "bis",
                                    })}
                                    isClearable={true}
                                    isSearchable={true}
                                />
                            </sc.Col>
                        </sc.Row>
                    </CaptionContainer>

                    <CaptionContainer
                        caption={intl.formatMessage({
                            id: "leaseme.power",
                            defaultMessage: "Leistung",
                        })}
                    >
                        <sc.Row>
                            <sc.Col>
                                <SingleChoiceFilter<number>
                                    value={filter.filter.minPs}
                                    options={filterOptionsMaxPs}
                                    onChange={(value) => {
                                        if (filter.setFilter) {
                                            filter.setFilter("minPs", value);
                                        }
                                    }}
                                    placeholder={intl.formatMessage({
                                        id: "leaseme.from",
                                        defaultMessage: "von",
                                    })}
                                    isClearable={true}
                                    isSearchable={true}
                                />
                            </sc.Col>
                            <sc.Col>
                                <SingleChoiceFilter<number>
                                    value={filter.filter.maxPs}
                                    options={filterOptionsMinPs}
                                    onChange={(value) => {
                                        if (filter.setFilter) {
                                            filter.setFilter("maxPs", value);
                                        }
                                    }}
                                    placeholder={intl.formatMessage({
                                        id: "leaseme.to",
                                        defaultMessage: "bis",
                                    })}
                                    isClearable={true}
                                    isSearchable={true}
                                />
                            </sc.Col>
                        </sc.Row>
                    </CaptionContainer>

                    <CaptionContainer
                        caption={intl.formatMessage({
                            id: "leaseme.bodyType.single",
                            defaultMessage: "Karosserieform",
                        })}
                    >
                        <MultipleChoiceFilter
                            values={filter.filter.bodyTypes}
                            options={convertToSelectOptions(searchResult.facets?.bodyTypes)}
                            onChange={(values) => {
                                if (filter.setFilter) {
                                    filter.setFilter("bodyTypes", values);
                                }
                            }}
                            placeholder={intl.formatMessage({
                                id: "leaseme.all",
                                defaultMessage: "Alle",
                            })}
                            isSearchable={true}
                            isClearable={true}
                            selectAll
                        />
                    </CaptionContainer>

                    <CaptionContainer
                        caption={intl.formatMessage({
                            id: "leaseme.fuel",
                            defaultMessage: "Kraftstoff",
                        })}
                    >
                        <MultipleChoiceFilter
                            values={filter.filter.fuelTypes}
                            options={convertToSelectOptions(searchResult.facets?.fuelTypes)}
                            onChange={(values) => {
                                if (filter.setFilter) {
                                    filter.setFilter("fuelTypes", values);
                                }
                            }}
                            placeholder={intl.formatMessage({
                                id: "leaseme.all",
                                defaultMessage: "Alle",
                            })}
                            isSearchable={true}
                            isClearable={true}
                            selectAll
                        />
                    </CaptionContainer>
                    <CaptionContainer
                        caption={intl.formatMessage({
                            id: "leaseme.province.single",
                            defaultMessage: "Bundesland",
                        })}
                    >
                        <MultipleChoiceFilter
                            values={filter.filter.provinces}
                            options={convertToSelectOptions(searchResult.facets?.provinces?.filter((province) => province.name.length > 0))}
                            onChange={(values) => {
                                if (filter.setFilter) {
                                    filter.setFilter("provinces", values);
                                }
                            }}
                            placeholder={intl.formatMessage({
                                id: "leaseme.all",
                                defaultMessage: "Alle",
                            })}
                            isSearchable={true}
                            isClearable={true}
                            selectAll
                        />
                    </CaptionContainer>
                    <sc.MobileContainer>
                        <SearchSort
                            caption={intl.formatMessage({
                                id: "leaseme.sorting",
                                defaultMessage: "Sortierung",
                            })}
                            variant="default"
                        />
                    </sc.MobileContainer>
                </sc.FilterGroup>
                <sc.ShowResultsButton>
                    <Button variant={ButtonVariant.Primary} width={ButtonWidth.FullWidth} size={ButtonSize.Big} onClick={handleShowResultsClick}>
                        {searchResult.loading ? (
                            <LoadingSpinner variant={LoadingSpinnerVariant.Small} />
                        ) : (
                            <FormattedNumber value={searchResult.totalCount} />
                        )}{" "}
                        <FormattedMessage
                            id="leaseme.results"
                            defaultMessage="{total, plural, =1 {{single}} other {{plural}}}"
                            values={{
                                total: searchResult.totalCount,
                                single: intl
                                    .formatMessage({
                                        id: "leaseme.results.single",
                                        defaultMessage: "Treffer",
                                    })
                                    .toUpperCase(),
                                plural: intl
                                    .formatMessage({
                                        id: "leaseme.results.plural",
                                        defaultMessage: "Treffer",
                                    })
                                    .toUpperCase(),
                            }}
                        />
                    </Button>

                    <sc.ResetButton>
                        <Button size={ButtonSize.Big} variant={ButtonVariant.Secondary} width={ButtonWidth.FullWidth} onClick={handleFilterReset}>
                            <sc.ResetButtonText>
                                <FormattedMessage id="leaseme.filter.reset" defaultMessage="Zurücksetzen" />
                            </sc.ResetButtonText>
                        </Button>
                    </sc.ResetButton>
                </sc.ShowResultsButton>
            </sc.Filter>
            {!filter.mobileFilterOpen && (
                <sc.AdjustFilterButton>
                    <sc.FilterButton variant={ButtonVariant.Primary} onClick={handleAdjustFilterClick}>
                        <FormattedMessage id="leaseme.filter.refineSearch" defaultMessage="Filter anpassen" />
                    </sc.FilterButton>
                </sc.AdjustFilterButton>
            )}
        </sc.Root>
    );
};
export default SearchFilter;
